import type { TimelineFeatures } from "@expert/features";
import type { Partner } from "@expert/shared-types";

import { reactAnalytics } from "@asurion-core/eventualize-react";
import { trackFullStoryEvent } from "@expert/monitoring";

import type { DefaultMessageKey } from "../shared-types";

import { getLastMessage, sessionGreetingKeys, useTimelineStore } from "../state";
import { addFirstOpenMessageIfNeeded } from "./addFirstDefaultMessage";
import { getDefaultMessage } from "./getDefaultMessage";

export interface SolveSessionProps {
    sessionId: string;
    callSid?: string;
    chatId?: string;
    partner: Partner;
    withCustomer: boolean;
    features: Pick<TimelineFeatures, "isProactiveEnabled">;
    messageKeyOverride?: DefaultMessageKey;
}

export const solveSessionStarted = ({
    sessionId,
    callSid,
    chatId,
    withCustomer,
    partner,
    features: { isProactiveEnabled },
    messageKeyOverride,
}: SolveSessionProps) => {
    const { expertId, addMessage, expertName } = useTimelineStore.getState();
    addFirstOpenMessageIfNeeded(sessionId, expertName, messageKeyOverride);
    const lastMessage = getLastMessage(sessionId);
    const hasSessionGreeting = sessionGreetingKeys.some((key) => key === lastMessage?.key);
    if (withCustomer && !hasSessionGreeting) {
        //TODO: Remove hardcoded mcafee greeting message
        if (partner === "mcafee") {
            addMessage(sessionId, {
                key: "newMcafeeCall",
                text: "Need help with software installations, pop-up blockers, or antivirus scanning? I can help with all of that and more!",
                type: "Default",
                id: `newMcafeeCall_${crypto.randomUUID()}`,
                tag: "New call",
                isUnread: true,
                status: "active",
            });
        } else {
            addMessage(sessionId, {
                ...getDefaultMessage("inCallDeviceInfo"),
                id: `deviceInfo_${crypto.randomUUID()}`,
            });
        }
    }

    const extraData = {
        expertAssistType: isProactiveEnabled ? "Proactive" : "Reactive",
    };

    void reactAnalytics.dispatcher
        .withIdentities({
            SessionId: sessionId,
            ExpertAssistSessionId: sessionId,
            ExpertId: expertId,
            ...(callSid && { CallSid: callSid }),
            ...(chatId && { ChatId: chatId }),
        })
        .withExtra({ tool: "expert-assist", partner, ...extraData, withCustomer })
        .dispatchBusinessEvent("ExpertAssistSessionStarted");
    trackFullStoryEvent("ExpertAssistSessionStarted", {
        sessionId,
        callSid,
        chatId,
        partner,
        ...extraData,
        withCustomer,
    });
};

export const solveSessionClosed = ({
    sessionId,
    callSid,
    chatId,
    partner,
    features: { isProactiveEnabled },
}: Omit<SolveSessionProps, "withCustomer">) => {
    const { expertId } = useTimelineStore.getState();

    const expertAssistType = isProactiveEnabled ? "Proactive" : "Reactive";
    void reactAnalytics.dispatcher
        .withIdentities({
            SessionId: sessionId,
            ExpertAssistSessionId: sessionId,
            ExpertId: expertId,
            ...(callSid && { CallSid: callSid }),
            ...(chatId && { ChatId: chatId }),
        })
        .withExtra({ tool: "expert-assist", partner, expertAssistType })
        .dispatchBusinessEvent("ExpertAssistSessionClosed");
    trackFullStoryEvent("ExpertAssistSessionClosed", { sessionId, callSid, chatId, partner, expertAssistType });
};
